/**
 * Internal CRM contract (A7Y-4973, epic A7Y-4971).
 *
 * The sales and partnership record that today lives in two hand-edited files in
 * a7y-ai-dev — `marketing/growth/pipeline.md` (the scoreboard and account
 * history) and `marketing/growth/helm-roster.md` (the dated index) — as one
 * record in the `a7y-crm` Firestore database (`CRM_FIRESTORE` in `./data-plane.ts`).
 *
 * The collection names live here, not in `COLLECTION` (`./enums.ts`): every
 * `COLLECTION` value must be classified in `scripts/staging-plane/copy/manifest.json`,
 * which describes the `(default)` database, and these collections are not in it.
 * `HELM_COLLECTIONS` (`packages/platform-health/src/helm/contract.ts`) is the precedent.
 *
 * Every date is an ISO string — `YYYY-MM-DD` for a calendar day, full ISO 8601
 * for an instant — and stays one on the wire: the Worker's serializer keeps
 * every string in a CRM context a `stringValue` (`firebase_firestore.ts`).
 * Counts and stale flags are computed when read, never stored.
 */

export const CRM_SCHEMA_VERSION = 1 as const;

export const CRM_COLLECTIONS = {
  accounts: 'accounts',
  contacts: 'contacts',
  events: 'events',
  rosterItems: 'rosterItems',
  slots: 'slots',
  weekPlans: 'weekPlans',
  followUps: 'followUps',
  leads: 'leads',
  meta: 'meta',
} as const;

export type CrmCollection = (typeof CRM_COLLECTIONS)[keyof typeof CRM_COLLECTIONS];

/** The funnel stage model in `pipeline.md` ("Funnel stage model"). */
export const CRM_STAGES = [
  'lead',
  'conversation',
  'demo',
  'trial',
  'proposal',
  'signed',
  'onboarded',
  'dormant',
  'closed',
] as const;
export type CrmStage = (typeof CRM_STAGES)[number];

/** Only enterprise, mid and agency count in the customer funnel; partner rows are counted separately. */
export const CRM_SEGMENTS = ['enterprise', 'mid', 'agency', 'partner'] as const;
export type CrmSegment = (typeof CRM_SEGMENTS)[number];

/** The roster's `Block` column: where the work happens. */
export const CRM_BLOCKS = ['demand', 'contact', 'decisions', 'maker'] as const;
export type CrmBlock = (typeof CRM_BLOCKS)[number];

/**
 * The roster's sections: next touches, follow-ups owed to a named person, scheduled decisions,
 * and (A7Y-5204) backlog triggers — an event to act on, due on its date fallback.
 */
export const CRM_ROSTER_ITEM_KINDS = ['touch', 'follow-up', 'decision', 'trigger'] as const;
export type CrmRosterItemKind = (typeof CRM_ROSTER_ITEM_KINDS)[number];

/**
 * A7Y-5203: an account's priority rank, highest first. `P5b` and `unavailable` are the
 * growth dashboard's own buckets; an account with no rank is P8 by the dashboard's rule.
 */
export const CRM_PRIORITIES = [
  'P1',
  'P2',
  'P3',
  'P4',
  'P5',
  'P5b',
  'P6',
  'P7',
  'P8',
  'unavailable',
] as const;
export type CrmPriority = (typeof CRM_PRIORITIES)[number];

/** `YYYY-MM-DD`. */
export type CrmDate = string;
/** Full ISO 8601 instant. */
export type CrmInstant = string;

/** `meta/{id}` documents: the import marker, the parked list (A7Y-4974) and the frozen prose of the two source files (A7Y-4976). */
export const CRM_META_IDS = { import: 'import', parked: 'parked', legacy: 'legacy' } as const;

/** A stale account's silence threshold in days. An assumption (A7Y-4974 D9); published in every `policy`. */
export const CRM_STALE_AFTER_DAYS = 14;
/** Firestore `IN` takes 30 values: the canonical id plus at most 29 aliases. */
export const CRM_MAX_ALIASES = 29;
export const CRM_SLOT_CAP = 10;
export const CRM_WEEK_PLANS_LIMIT_DEFAULT = 8;
export const CRM_WEEK_PLANS_LIMIT_MAX = 52;

export type CrmImportStatus = 'importing' | 'complete';

/** Who last wrote a document: a platform user, an MCP client acting for one, the importer, or HELM landing an approved proposal. */
export interface CrmAuthor {
  userId: string | null;
  email: string | null;
  via: 'admin' | 'mcp' | 'import' | 'helm';
}

export interface CrmStamped {
  schemaVersion: typeof CRM_SCHEMA_VERSION;
  createdAt: CrmInstant;
  updatedAt: CrmInstant;
  updatedBy: CrmAuthor;
}

/** `accounts/{id}` — id is the existing `acct-` slug, so every `#acct-` anchor survives. */
export interface CrmAccountDoc extends CrmStamped {
  id: string;
  name: string;
  stage: CrmStage;
  /** Free text the scoreboard's stage cell carries beside the stage, e.g. "ambassador; Elite 7/22". */
  stageNote: string | null;
  segment: CrmSegment;
  /** Stage `closed` only: why the account said no, or we did (the funnel model records reason and date). */
  closedReason: string | null;
  lastTouch: CrmDate | null;
  lastTouchNote: string | null;
  nextAction: string | null;
  /** A `Watch:` next action: no touch owed until the watched condition changes. */
  watch: string | null;
  /** Conditions that hold the account (e.g. a feature not yet cleared by Yevhen). */
  gates: string[];
  /** Links the scoreboard's `Where` column and the section carry: call notes, dossiers. */
  links: string[];
  /** Free-form account notes that are not dated history (the section's `Notes:` lines). */
  notes: string | null;
  /** Every former id of the account (A7Y-4974 D6); each is also a tombstone document at `accounts/{formerId}`. */
  aliases: string[];
  /** A7Y-5203: the priority rank; absent or null = not ranked (P8 by the dashboard's rule). */
  priority?: CrmPriority | null;
}

/** `contacts/{id}`. */
export interface CrmContactDoc extends CrmStamped {
  id: string;
  accountId: string | null;
  name: string;
  role: string | null;
  email: string | null;
  /** Free text: how the contact came in, whether they left, anything the scoreboard's contact cell says. */
  note: string | null;
  /** The contact has left the account; the scoreboard strikes the name through. */
  departed: boolean;
  /** The account's primary contact; at most one per account. */
  primary: boolean;
  /** The contact this one reports to (a contact id), or null. */
  reportsTo: string | null;
}

/** `events/{id}` — the dated history lines of an account's section. */
export interface CrmEventDoc extends CrmStamped {
  id: string;
  accountId: string;
  date: CrmDate;
  /** Where the fact came from: a call, the inbox, a BD meeting, a Slack message. */
  source: string;
  /** The channel the exchange happened on, when there was one (email, LinkedIn, video call). */
  channel: string | null;
  /** Who reported or wrote the fact (e.g. "Yevhen"), as the line attributes it. */
  author: string | null;
  text: string;
  links: string[];
}

/**
 * `rosterItems/{id}` — one row of the roster: a dated next touch, a follow-up a
 * named person is waiting on, or a scheduled decision. An account's next date is
 * derived from its open items, never stored on the account.
 */
export interface CrmRosterItemDoc extends CrmStamped {
  id: string;
  kind: CrmRosterItemKind;
  accountId: string | null;
  /** The row's who/what cell: what was said or done, never a suggestion (A7Y-5084). */
  title: string;
  /**
   * A session's suggestion for the row, kept out of `title` so no later reader
   * takes it for a fact or an ask (A7Y-5084). A reader repeats it verbatim as a
   * suggestion or drops it. Absent on rows written before A7Y-5084.
   */
  proposal?: string | null;
  due: CrmDate;
  block: CrmBlock;
  /** The row's `Where`: one link. */
  where: string | null;
  status: 'open' | 'closed';
  /** Closed rows only: the roster's Closed table (`Route`, `Outcome`, `Closed`). */
  route: string | null;
  outcome: string | null;
  closedOn: CrmDate | null;
  /**
   * The HELM item key the row carried in `helm-roster.md` before the import (A7Y-4976 D7), so a `helm_actions`
   * document written against that key still resolves. Null for a row created in the CRM.
   */
  legacyKey: string | null;
}

/** `slots/{id}` — the active conversation slots (cap 10); id is the slot number. */
export interface CrmSlotDoc extends CrmStamped {
  id: string;
  slot: number;
  accountId: string | null;
  why: string | null;
}

export interface CrmWeekPlanMove {
  move: string;
  accountId: string | null;
  owner: string;
  day: string;
}

/** `weekPlans/{weekStart}` — the moves set at the Monday ritual; id is the Monday's date. */
export interface CrmWeekPlanDoc extends CrmStamped {
  id: CrmDate;
  weekStart: CrmDate;
  note: string | null;
  moves: CrmWeekPlanMove[];
}

/** `followUps/{id}` — `pipeline.md`'s personal follow-ups table. */
export interface CrmFollowUpDoc extends CrmStamped {
  id: string;
  person: string;
  company: string | null;
  lastContact: CrmDate | null;
  channel: string | null;
  sent: string | null;
  status: string | null;
}

/** `leads/{id}` — inbound leads not yet on the scoreboard (e.g. the TRMNL4 webinar leads). */
export interface CrmLeadDoc extends CrmStamped {
  id: string;
  name: string;
  email: string | null;
  app: string | null;
  source: string;
  linearTicket: string | null;
  engineeringStatus: string | null;
  outreachStatus: string | null;
  accountId: string | null;
}

// ======================================================================================
// A7Y-4974 — the `/admin/crm` route contract.
// ======================================================================================

/** `accounts/{formerId}` after a rename: reserves the former id and points at the canonical document (D6). */
export interface CrmAccountAliasDoc extends CrmStamped {
  id: string;
  aliasOf: string;
  renamedAt: CrmInstant;
}
export type CrmAccountRecord = CrmAccountDoc | CrmAccountAliasDoc;

export type CrmImportCounts = Record<Exclude<CrmCollection, 'meta'>, number>;

/** `meta/import` — claimed before the first record is written, completed after the last (D1). */
export interface CrmImportMarkerDoc extends CrmStamped {
  id: typeof CRM_META_IDS.import;
  status: CrmImportStatus;
  source: string;
  payloadSha256: string;
  claimedAt: CrmInstant;
  importedAt: CrmInstant | null;
  counts: CrmImportCounts;
  /**
   * The import execution that owns the claim: written on claim and rewritten when a re-POST resumes,
   * which supersedes the execution before it. Its fence document is `meta/import-fence-{executionId}`.
   */
  executionId: string;
}

/** `meta/import-fence-{executionId}` — exists only while that execution owns the import; every chunk it writes must find it. */
export interface CrmImportFenceDoc extends CrmStamped {
  id: string;
  executionId: string;
}

/**
 * `meta/renaming-{sourceId}` while a rename is in flight (every account edit deletes it, which fails the rename's
 * swap), and `meta/renamed-{sourceId}` for good once the swap committed (every account edit asserts it is absent,
 * so no edit that resolved the former id lands on the tombstone).
 */
export interface CrmRenameMarkerDoc extends CrmStamped {
  id: string;
  sourceId: string;
  newId: string;
}

export interface CrmParkedEntry {
  accountId: string;
  why: string | null;
}

/** `meta/parked` — the parked list, replaced as a whole. */
export interface CrmParkedDoc extends CrmStamped {
  id: typeof CRM_META_IDS.parked;
  entries: CrmParkedEntry[];
}

export type CrmImportRecord<T extends CrmStamped> = Omit<T, keyof CrmStamped>;

/**
 * One account as `POST /admin/crm/import` takes it: `id`, `name` and `segment` are required and the rest defaults.
 * `aliases` may be left out or empty — the importer starts every account with none (a rename creates them).
 */
export type CrmImportAccountInput = Pick<
  CrmImportRecord<CrmAccountDoc>,
  'id' | 'name' | 'segment'
> &
  Partial<Omit<CrmImportRecord<CrmAccountDoc>, 'id' | 'name' | 'segment' | 'aliases'>> & {
    /** Only an empty list (what an export of a normalized account carries); the importer never accepts former ids. */
    aliases?: [];
  };

/**
 * Where a block of the source files' prose sits when the files are rendered again (A7Y-4976 D20). The order is the
 * order of the rendered pipeline file, then the roster.
 */
export const CRM_LEGACY_POSITIONS = [
  'pipeline-top',
  'pipeline-slots',
  'pipeline-week-plans',
  'pipeline-scoreboard',
  'pipeline-accounts',
  'pipeline-accounts-end',
  'pipeline-follow-ups',
  'pipeline-leads',
  'pipeline-waitlist',
  'pipeline-end',
  'roster-top',
  'roster-prospects',
  'roster-follow-ups',
  'roster-decisions',
  'roster-closed',
] as const;
export type CrmLegacyPosition = (typeof CRM_LEGACY_POSITIONS)[number];

/** One block of prose from the source files: `heading` is the `## ` text, or null for a block of body-only lines. */
export interface CrmLegacySection {
  position: CrmLegacyPosition;
  heading: string | null;
  body: string;
}

/** The prose the CRM has no field for: the header log, the refresh log and the durable sections, verbatim. */
export interface CrmLegacyInput {
  pipelineHeaderLog: string | null;
  refreshLog: string | null;
  sections: CrmLegacySection[];
}

/** `meta/legacy` — written by the import, read by the renderers; no route reads or edits it. */
export interface CrmLegacyDoc extends CrmStamped, CrmLegacyInput {
  id: typeof CRM_META_IDS.legacy;
}

/** The body of `POST /admin/crm/import`, as a caller sends it. */
export interface CrmImportPayload {
  source: string;
  accounts: CrmImportAccountInput[];
  contacts: CrmImportRecord<CrmContactDoc>[];
  events: CrmImportRecord<CrmEventDoc>[];
  rosterItems: CrmImportRecord<CrmRosterItemDoc>[];
  slots: CrmImportRecord<CrmSlotDoc>[];
  weekPlans: CrmImportRecord<CrmWeekPlanDoc>[];
  followUps: CrmImportRecord<CrmFollowUpDoc>[];
  leads: CrmImportRecord<CrmLeadDoc>[];
  parked: CrmParkedEntry[];
  /** Left out, it is an empty block. */
  legacy?: CrmLegacyInput;
}

/** The payload after parsing: every default filled in, every account carrying its empty `aliases`. */
export interface CrmNormalizedImportPayload extends Omit<CrmImportPayload, 'accounts' | 'legacy'> {
  accounts: CrmImportRecord<CrmAccountDoc>[];
  legacy: CrmLegacyInput;
}

export const CRM_ERROR_CODES = {
  unavailable: 'CRM_UNAVAILABLE',
  notProvisioned: 'CRM_NOT_PROVISIONED',
  notImported: 'CRM_NOT_IMPORTED',
  alreadyImported: 'CRM_ALREADY_IMPORTED',
  importInProgress: 'CRM_IMPORT_IN_PROGRESS',
  invalid: 'CRM_INVALID',
  notFound: 'CRM_NOT_FOUND',
  conflict: 'CRM_CONFLICT',
} as const;
export type CrmErrorCode = (typeof CRM_ERROR_CODES)[keyof typeof CRM_ERROR_CODES];

export interface CrmErrorBody {
  code: CrmErrorCode;
  message: string;
  errors?: string[];
  marker?: CrmImportMarkerDoc | null;
}

export interface CrmPolicy {
  staleAfterDays: number;
}

// Reads — every read carries `imported`; the three that carry `stale` carry `policy` (D9).
export interface CrmCounts {
  byStage: Record<CrmStage, number>;
  bySegment: Record<CrmSegment, number>;
  /** The customer funnel — enterprise, mid and agency rows — by stage. */
  customerByStage: Record<CrmStage, number>;
  /** Partner rows by stage; counted apart from the customer funnel (pipeline.md "Segments"). */
  partnerByStage: Record<CrmStage, number>;
}
export interface CrmSummaryResponse {
  imported: boolean;
  policy: CrmPolicy;
  marker: CrmImportMarkerDoc | null;
  counts: CrmCounts;
  stale: number;
  openRosterItems: number;
}
/** A contact as the account list carries it. */
export interface CrmContactRef {
  id: string;
  name: string;
  departed: boolean;
}
export interface CrmAccountListItem {
  account: CrmAccountDoc;
  nextDate: CrmDate | null;
  stale: boolean;
  /** The account's primary contact, or null. */
  primaryContact: CrmContactRef | null;
  /** Every contact on the account, sorted by id. */
  contacts: CrmContactRef[];
  /** A7Y-5204: the account's earliest open trigger item, or null. */
  trigger: { id: string; title: string; due: CrmDate } | null;
}
export interface CrmAccountListResponse {
  imported: boolean;
  policy: CrmPolicy;
  accounts: CrmAccountListItem[];
}
export interface CrmAccountDetailResponse {
  imported: boolean;
  policy: CrmPolicy;
  account: CrmAccountDoc | null;
  contacts: CrmContactDoc[];
  events: CrmEventDoc[];
  rosterItems: CrmRosterItemDoc[];
  nextDate: CrmDate | null;
  stale: boolean;
}
export interface CrmEventListResponse {
  imported: boolean;
  events: CrmEventDoc[];
}
export interface CrmRosterListResponse {
  imported: boolean;
  items: CrmRosterItemDoc[];
}
export interface CrmSlotsResponse {
  imported: boolean;
  slots: CrmSlotDoc[];
  parked: CrmParkedEntry[];
}
export interface CrmWeekPlanListResponse {
  imported: boolean;
  weekPlans: CrmWeekPlanDoc[];
  /** Every stored plan, before `limit`/`offset` (0 before the import). */
  total: number;
}
/** `GET /week-plans/:weekStart` before the import is `{ imported: false, weekPlan: null }`; a write answers the written plan. */
export interface CrmWeekPlanResponse {
  imported: boolean;
  weekPlan: CrmWeekPlanDoc | null;
}
export interface CrmFollowUpListResponse {
  imported: boolean;
  followUps: CrmFollowUpDoc[];
}
export interface CrmLeadListResponse {
  imported: boolean;
  leads: CrmLeadDoc[];
}

// Writes — every successful write is 200 with the written document(s) (D16).
export interface CrmAccountMutationResponse {
  account: CrmAccountDoc;
  event: CrmEventDoc | null;
}
export type CrmRenameRepointed = Record<
  'contacts' | 'events' | 'rosterItems' | 'slots' | 'leads' | 'weekPlans' | 'parked' | 'aliases',
  number
>;
export interface CrmAccountRenameResponse {
  account: CrmAccountDoc;
  alias: CrmAccountAliasDoc;
  repointed: CrmRenameRepointed;
  commits: number;
}
/** `demoted`: sibling ids set primary=false (empty except on `/primary`). */
export interface CrmContactResponse {
  contact: CrmContactDoc;
  demoted: string[];
}
export interface CrmEventResponse {
  event: CrmEventDoc;
}
export interface CrmRosterItemResponse {
  item: CrmRosterItemDoc;
}
export interface CrmParkedResponse {
  parked: CrmParkedEntry[];
}
export interface CrmFollowUpResponse {
  followUp: CrmFollowUpDoc;
}
export interface CrmLeadResponse {
  lead: CrmLeadDoc;
}
export interface CrmImportResponse {
  marker: CrmImportMarkerDoc;
  resumed: boolean;
}
export interface CrmDeleteResponse {
  deleted: true;
  id: string;
}

// Inputs (bodies). `?` = optional; omitted optional strings default to null, omitted arrays to [], unless stated.
export interface CrmAccountCreateInput {
  id?: string;
  name: string;
  stage?: CrmStage;
  stageNote?: string | null;
  segment: CrmSegment;
  lastTouch?: CrmDate | null;
  lastTouchNote?: string | null;
  nextAction?: string | null;
  watch?: string | null;
  gates?: string[];
  links?: string[];
  notes?: string | null;
}
export interface CrmStageSetInput {
  stage: CrmStage;
  stageNote?: string | null;
  note?: string;
  confirmedBy?: string;
  date?: CrmDate;
  requestId?: string;
}
export interface CrmLastTouchInput {
  lastTouch: CrmDate;
  lastTouchNote?: string | null;
}
export interface CrmNextActionInput {
  nextAction: string | null;
}
export interface CrmSegmentInput {
  segment: CrmSegment;
}
/** A7Y-5203: the rank to set, or null to clear it. */
export interface CrmPriorityInput {
  priority: CrmPriority | null;
}
/** Cadence or trigger as text; null clears. */
export interface CrmWatchInput {
  watch: string | null;
}
export interface CrmCloseInput {
  closedReason: string;
  note?: string;
  confirmedBy?: string;
  date?: CrmDate;
  requestId?: string;
}
export interface CrmRenameInput {
  newId: string;
}
export interface CrmContactUpsertInput {
  id?: string;
  accountId: string | null;
  name: string;
  role?: string | null;
  email?: string | null;
  note?: string | null;
  departed?: boolean;
  primary?: boolean;
  reportsTo?: string | null;
}
/** `departed` defaults to true. */
export interface CrmDepartedInput {
  departed?: boolean;
}
export interface CrmReportsToInput {
  reportsTo: string | null;
}
export interface CrmEventAppendInput {
  accountId: string;
  date: CrmDate;
  source: string;
  channel?: string | null;
  author?: string | null;
  text: string;
  links?: string[];
  requestId?: string;
}
export interface CrmRosterAddInput {
  id?: string;
  kind: CrmRosterItemKind;
  accountId?: string | null;
  title: string;
  proposal?: string | null;
  due: CrmDate;
  block: CrmBlock;
  where?: string | null;
}
export interface CrmRosterRedateInput {
  due: CrmDate;
}
export interface CrmRosterCloseInput {
  route: string;
  outcome: string;
  closedOn?: CrmDate;
}
export interface CrmRosterLinkInput {
  accountId: string;
}
export interface CrmSlotsReplaceInput {
  slots: Array<{ slot: number; accountId: string | null; why: string | null }>;
}
export interface CrmParkedReplaceInput {
  entries: CrmParkedEntry[];
}
export interface CrmWeekPlanWriteInput {
  note: string | null;
  moves: CrmWeekPlanMove[];
}
export type CrmFollowUpUpdateInput = Omit<CrmImportRecord<CrmFollowUpDoc>, 'id'>;
export type CrmLeadUpdateInput = Omit<CrmImportRecord<CrmLeadDoc>, 'id'>;
